import {
  Anchor,
  Box,
  Flex,
  NumberFormatter,
  Progress,
  Stack,
  Text,
} from "@mantine/core";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import type { AllocationBarChartProps } from "../../types";
import { allocationChartColor } from "../../utils/allocationChartColors";
import { isMobileDevice } from "../../utils/useDeviceDetection";
import classes from "./index.module.css";

const isMobile = isMobileDevice();

export function AllocationBarChart({
  items,
  withLegend = true,
}: AllocationBarChartProps) {
  const slices = items.map((item, index) => {
    // The link icon is tied to the last word so it never wraps onto a line of its own.
    const words = item.name.trim().split(/\s+/);
    const lastWord = words.pop() ?? "";
    const head = words.length > 0 ? `${words.join(" ")} ` : "";

    return { ...item, color: allocationChartColor(index), head, lastWord };
  });

  return (
    <Stack gap="md">
      <Progress.Root size={40} radius="md" aria-hidden="true">
        {slices.map((slice) => (
          <Progress.Section
            key={`${slice.name}-${slice.isin ?? ""}`}
            value={slice.share}
            color={slice.color}
          />
        ))}
      </Progress.Root>

      {withLegend && (
        <Box className={classes.legendColumn}>
          <Stack component="ul" gap="sm" className={classes.legend}>
            {slices.map((slice) => (
              <Flex
                component="li"
                key={`${slice.name}-${slice.isin ?? ""}`}
                justify="space-between"
                align={isMobile ? "flex-start" : "center"}
                gap="md"
                wrap="nowrap"
              >
                <Flex
                  align={isMobile ? "flex-start" : "center"}
                  gap="sm"
                  style={{ minWidth: 0, flex: 1 }}
                >
                  <Box
                    className={`${classes.legendIndicator} ${isMobile ? classes.firstLine : ""}`}
                    bg={slice.color}
                  />
                  <Text
                    fz={isMobile ? "sm" : "md"}
                    style={{ overflowWrap: "anywhere" }}
                  >
                    {slice.head}
                    <span className={classes.lastWord}>
                      {slice.lastWord}
                      {slice.isin && (
                        <Anchor
                          c="dimmed"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${slice.name}, fondinformation hos Morningstar (öppnas i ny flik)`}
                          href={`${import.meta.env.VITE_MORNINGSTAR_URL}&documenttype=1&language=${import.meta.env.VITE_MORNINGSTAR_LANGUAGE}&isin=${slice.isin}`}
                          className={classes.legendLink}
                        >
                          <ArrowTopRightOnSquareIcon
                            height="12px"
                            width="12px"
                          />
                        </Anchor>
                      )}
                    </span>
                  </Text>
                </Flex>
                <Text
                  fz={isMobile ? "sm" : "md"}
                  className={classes.legendValue}
                >
                  <NumberFormatter
                    value={slice.share}
                    decimalScale={2}
                    decimalSeparator=","
                    suffix=" %"
                  />
                </Text>
              </Flex>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
